import * as THREE from 'three';
import { box, weld, makeRng } from './VoxelFactory.js';
import { Lane, planLane } from '../gameplay/Lane.js';
import { buildRailcarGeometry, buildFlatcarGeometry } from '../gameplay/RollingStock.js';
import { Cobra } from '../gameplay/Cobra.js';
import { FloodSystem } from '../gameplay/FloodSystem.js';
import { ElectricZapManager } from '../gameplay/ElectricZap.js';
import { CurryItem } from '../gameplay/CurryItem.js';
import { gameState } from '../core/GameState.js';
import {
  ROW, ROW_DEPTH, FIELD_HALF, TILE_X, LEVEL, COLORS, CURRY,
} from '../core/Constants.js';

export const EDGE = FIELD_HALF + 2.6;       // where rolling stock is clipped away
const GROUND_HALF = EDGE + 0.4;
const EMBANKMENT_W = 16;
const APRON_ROWS = 8;                       // grass carried past both banks so
                                            // the world never ends in mid-air

/** Lay out the row list for a level: bank, rail block, median, yard, rail, goal. */
function planRows(level) {
  const railA = Math.min(LEVEL.RAIL_A[1], LEVEL.RAIL_A[0] + Math.floor((level - 1) / 2));
  const freight = Math.min(
    LEVEL.FREIGHT_COUNT[1],
    LEVEL.FREIGHT_COUNT[0] + Math.floor((level - 1) / 3),
  );
  const railB = Math.min(LEVEL.RAIL_B[1], LEVEL.RAIL_B[0] + Math.floor((level - 1) / 4));

  const rows = [{ type: ROW.GRASS, kind: 'bank' }];
  for (let i = 0; i < railA; i++) rows.push({ type: ROW.RAIL });
  for (let i = 0; i < LEVEL.MEDIAN_ROWS; i++) rows.push({ type: ROW.GRASS, kind: 'median' });
  for (let i = 0; i < freight; i++) rows.push({ type: ROW.FREIGHT });
  for (let i = 0; i < railB; i++) rows.push({ type: ROW.RAIL });
  rows.push({ type: ROW.GRASS, kind: 'goal' });
  return rows;
}

export class LevelBuilder {
  constructor(scene, materials) {
    this.scene = scene;
    this.materials = materials;
    this.lanes = [];
    this.staticMesh = null;
    this.railcarCache = new Map();
    this.flatcarGeo = null;
    this.cobra = new Cobra(scene, materials.solid, materials.shadow);
    this.floodSystem = new FloodSystem(scene, materials);
    this.zapManager = new ElectricZapManager(scene);
    this.curryItem = new CurryItem(scene, materials.solid, materials.shadow);
    this.laneGroup = new THREE.Group();
    scene.add(this.laneGroup);
  }

  setPlayer(player) {
    this.player = player;
    if (this.floodSystem) this.floodSystem.setPlayer(player);
  }

  railcarGeometry(colorIndex, lead) {
    const key = `${colorIndex}:${lead ? 1 : 0}`;
    let geo = this.railcarCache.get(key);
    if (!geo) {
      geo = buildRailcarGeometry(COLORS.TRAIN[colorIndex % COLORS.TRAIN.length], lead);
      this.railcarCache.set(key, geo);
    }
    return geo;
  }

  flatcarGeometry() {
    if (!this.flatcarGeo) this.flatcarGeo = buildFlatcarGeometry();
    return this.flatcarGeo;
  }

  build(level) {
    this.teardown();

    const rng = makeRng(level * 2654435761 + 12345);
    const specs = planRows(level);
    const parts = [];
    const rows = [];

    specs.forEach((spec, row) => {
      const z = -row * ROW_DEPTH;
      if (spec.type === ROW.GRASS) this.buildGrass(parts, row, z, spec.kind, rng);
      else if (spec.type === ROW.RAIL) this.buildRailBed(parts, z);
      else this.buildYard(parts, z);
      rows.push({ type: spec.type, kind: spec.kind, row, lane: null });
    });

    this.buildApron(parts, specs.length, rng);
    this.buildEmbankments(parts, specs);

    this.staticMesh = new THREE.Mesh(weld(parts), this.materials.scenery);
    this.staticMesh.frustumCulled = false;
    this.scene.add(this.staticMesh);

    // Moving stock.
    const deps = {
      material: this.materials.solid,
      scenery: this.materials.scenery,
      signalOn: this.materials.signalOn,
      signalOff: this.materials.signalOff,
      railcarGeometry: (ci, lead) => this.railcarGeometry(ci, lead),
      flatcarGeometry: () => this.flatcarGeometry(),
    };

    let railIndex = Math.floor(rng() * COLORS.TRAIN.length);
    specs.forEach((spec, row) => {
      if (spec.type === ROW.GRASS) return;
      const plan = planLane(rng, { row, type: spec.type, level });
      // Step the livery every rail lane so two touching roads never match —
      // the colour is how you tell one lane's traffic from the next.
      if (spec.type === ROW.RAIL) plan.colorIndex = railIndex++ % COLORS.TRAIN.length;
      const lane = new Lane(plan, deps);
      this.laneGroup.add(lane.group);
      this.lanes.push(lane);
      rows[row].lane = lane;
      rows[row].dir = lane.dir;
      rows[row].speed = lane.speed;
    });

    gameState.level.rows = rows;
    gameState.level.goalRow = rows.length - 1;
    this.cobra.setLevelRows(rows);
    this.floodSystem.setLanes(this.lanes);
    this.zapManager.setRows(rows);

    // Randomly spawn a bowl of curry on grass/median row
    if (rng() < (CURRY.CHANCE ?? 0.85)) {
      const grassRows = rows.filter((r) => r.type === ROW.GRASS && r.row > 0 && r.row < gameState.level.goalRow);
      const targetRowObj = grassRows.length > 0 ? grassRows[Math.floor(rng() * grassRows.length)] : rows[1];
      const curryRow = targetRowObj ? targetRowObj.row : Math.floor(rows.length / 2);
      const curryX = (Math.floor(rng() * 9) - 4) * TILE_X;
      this.curryItem.spawn(curryX, curryRow);
    }

    return rows;
  }

  // ── static row geometry ────────────────────────────────────────────────

  buildGrass(parts, row, z, kind, rng) {
    const base = kind === 'median' ? COLORS.MEDIAN
      : row % 2 === 0 ? COLORS.GRASS_A : COLORS.GRASS_B;
    parts.push(box(GROUND_HALF * 2, 0.4, ROW_DEPTH, base, 0, -0.2, z));
    parts.push(box(GROUND_HALF * 2, 0.12, 0.18, COLORS.GRASS_EDGE, 0, 0.01, z - ROW_DEPTH / 2));
    parts.push(box(GROUND_HALF * 2, 0.12, 0.18, COLORS.GRASS_EDGE, 0, 0.01, z + ROW_DEPTH / 2));

    if (kind === 'goal') {
      // Hedge blocks along the far bank — the finish line reads as a park.
      for (let i = -6; i <= 6; i++) {
        if ((i + 6) % 2) continue;
        parts.push(box(TILE_X * 1.3, 1.1, ROW_DEPTH * 0.62, COLORS.BUSH, i * TILE_X * 1.55, 0.55, z));
      }
    } else if (kind === 'bank') {
      for (let i = -7; i <= 7; i++) {
        if ((i + 7) % 3) continue;
        parts.push(box(TILE_X * 0.5, 0.14, ROW_DEPTH * 0.3, COLORS.GRASS_EDGE, i * TILE_X * 1.2, 0.02, z + rng() * 0.6 - 0.3));
      }
    }
  }

  buildRailBed(parts, z) {
    parts.push(box(GROUND_HALF * 2, 0.4, ROW_DEPTH, COLORS.BALLAST, 0, -0.2, z));
    const step = 1.25;
    const count = Math.floor((GROUND_HALF * 2) / step);
    for (let i = 0; i < count; i++) {
      const x = -GROUND_HALF + step * 0.5 + i * step;
      parts.push(box(0.5, 0.16, ROW_DEPTH * 0.72, COLORS.SLEEPER, x, 0.05, z));
    }
    for (const dz of [-1, 1]) {
      parts.push(box(GROUND_HALF * 2, 0.2, 0.18, COLORS.RAIL, 0, 0.14, z + dz * 0.78));
    }
  }

  buildYard(parts, z) {
    // Electrified yard deck: yellow with black chevrons, a dark pit down the middle.
    parts.push(box(GROUND_HALF * 2, 0.4, ROW_DEPTH, COLORS.YARD_DECK, 0, -0.2, z));
    parts.push(box(GROUND_HALF * 2, 0.3, ROW_DEPTH * 0.44, COLORS.YARD_PIT, 0, -0.18, z));
    const step = 1.6;
    const count = Math.floor((GROUND_HALF * 2) / step);
    for (let i = 0; i < count; i++) {
      const x = -GROUND_HALF + step * 0.5 + i * step;
      for (const dz of [-1, 1]) {
        parts.push(box(0.7, 0.14, 0.34, COLORS.YARD_STRIPE, x, 0.02, z + dz * ROW_DEPTH * 0.36));
      }
    }
    for (const dz of [-1, 1]) {
      parts.push(box(GROUND_HALF * 2, 0.24, 0.16, COLORS.RAIL, 0, 0.06, z + dz * 1.0));
    }
  }

  /** Plain grass beyond both banks so the camera never sees the world edge. */
  buildApron(parts, rowCount, rng) {
    const depth = APRON_ROWS * ROW_DEPTH;

    for (const side of [-1, 1]) {
      // side -1 = near bank (behind the player), +1 = far bank (past the goal)
      const edgeRow = side < 0 ? -1 : rowCount;
      const near = -edgeRow * ROW_DEPTH;
      const outward = side < 0 ? 1 : -1;             // +z is away on the near side
      const zc = near + outward * (depth / 2 - ROW_DEPTH / 2);

      parts.push(box(GROUND_HALF * 2, 0.4, depth, COLORS.GRASS_B, 0, -0.2, zc));

      // Treeline closing the horizon, so the ground never just stops.
      const backZ = near + outward * (depth - ROW_DEPTH);
      parts.push(box(GROUND_HALF * 2 + 32, 7.5, ROW_DEPTH * 2, COLORS.GRASS_EDGE, 0, 3.5, backZ));
      parts.push(box(GROUND_HALF * 2 + 32, 0.6, ROW_DEPTH * 2, COLORS.BUSH, 0, 7.4, backZ));

      // Lineside fence a row out from the bank.
      const fenceZ = near + outward * ROW_DEPTH * 1.2;
      for (const y of [0.85, 0.45]) {
        parts.push(box(GROUND_HALF * 2, 0.1, 0.22, COLORS.GRASS_EDGE, 0, y, fenceZ));
      }
      for (let x = -GROUND_HALF + 1; x < GROUND_HALF; x += 3.4) {
        parts.push(box(0.18, 1.1, 0.28, COLORS.GRASS_EDGE, x, 0.55, fenceZ));
      }

      // Scattered trees between the fence and the treeline.
      for (let i = 0; i < 12; i++) {
        const x = -GROUND_HALF + 1.5 + rng() * (GROUND_HALF * 2 - 3);
        if (Math.abs(x) < 4) continue;             // keep the spawn lane clear
        const z = fenceZ + outward * (1.8 + rng() * (depth - ROW_DEPTH * 4));
        const h = 1.6 + rng() * 1.4;
        parts.push(box(0.5, h, 0.5, COLORS.SLEEPER, x, h / 2, z));
        parts.push(box(2.0, 1.7, 2.0, COLORS.BUSH, x, h + 0.7, z));
      }
    }
  }

  buildEmbankments(parts, specs) {
    const depth = (specs.length + APRON_ROWS * 2) * ROW_DEPTH + ROW_DEPTH;
    const zc = -((specs.length - 1) * ROW_DEPTH) / 2;
    for (const side of [-1, 1]) {
      const x = side * (GROUND_HALF + EMBANKMENT_W / 2);
      parts.push(box(EMBANKMENT_W, 6.5, depth, COLORS.GRASS_EDGE, x, 3.0, zc));
      parts.push(box(EMBANKMENT_W, 0.5, depth, COLORS.BUSH, x, 6.4, zc));

      // Tunnel mouths where the stock appears and vanishes.
      specs.forEach((spec, row) => {
        if (spec.type === ROW.GRASS) return;
        const z = -row * ROW_DEPTH;
        const mx = side * (GROUND_HALF + 0.9);
        parts.push(box(2.0, 4.2, ROW_DEPTH - 0.15, COLORS.TUNNEL, mx, 1.9, z));
        parts.push(box(0.5, 4.9, ROW_DEPTH + 0.4, COLORS.CHASSIS, side * (GROUND_HALF + 0.05), 2.2, z));
      });
    }
  }

  // ── lifecycle ──────────────────────────────────────────────────────────

  update(dt, elapsed) {
    for (const lane of this.lanes) lane.update(dt, elapsed);
    this.cobra.update(dt, elapsed);
    this.floodSystem.update(dt, elapsed);
    this.zapManager.update(dt);
    this.curryItem?.update(dt, elapsed);
  }

  teardown() {
    this.cobra?.reset();
    this.floodSystem?.reset();
    this.zapManager?.clear();
    this.curryItem?.reset();
    for (const lane of this.lanes) lane.dispose();
    this.lanes.length = 0;
    this.laneGroup.clear();
    if (this.staticMesh) {
      this.staticMesh.removeFromParent();
      this.staticMesh.geometry.dispose();
      this.staticMesh = null;
    }
  }

  dispose() {
    this.teardown();
    this.cobra?.dispose();
    this.floodSystem?.dispose();
    this.zapManager?.dispose();
    this.curryItem?.dispose();
    for (const g of this.railcarCache.values()) g.dispose();
    this.railcarCache.clear();
    if (this.flatcarGeo) { this.flatcarGeo.dispose(); this.flatcarGeo = null; }
    this.laneGroup.removeFromParent();
  }
}
